import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getVisibleSections } from '../../config/navigation';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Command palette (Ctrl+K) for quick navigation across all ERP pages.
 * Searches visible nav items based on user's role.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profile } = useAuth();

  // Global keyboard shortcut: Ctrl+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
        setQuery('');
        setSelectedIdx(0);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Get all visible pages for the current role
  const allItems = useCallback(() => {
    if (!profile) return [];
    const sections = getVisibleSections(profile.role);
    return sections.flatMap(s =>
      s.items.map(item => ({
        label: t(item.labelKey, item.label),
        section: t(s.titleKey, s.title),
        path: item.path,
      }))
    );
  }, [profile, t]);

  const items = allItems();
  const filtered = query
    ? items.filter(i =>
        i.label.toLowerCase().includes(query.toLowerCase()) ||
        i.section.toLowerCase().includes(query.toLowerCase())
      )
    : items.slice(0, 15);

  const handleSelect = (path: string) => {
    navigate(path);
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && filtered[selectedIdx]) {
      handleSelect(filtered[selectedIdx].path);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)}>
      <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
          <Search className="w-4 h-4 text-slate-600 dark:text-slate-500 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setSelectedIdx(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Search pages..."
            className="flex-1 bg-transparent text-slate-900 dark:text-white text-sm placeholder-slate-400 dark:placeholder-slate-500 outline-none"
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded text-[10px] text-slate-600 dark:text-slate-500">
            ESC
          </kbd>
          <button onClick={() => setOpen(false)} className="text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white sm:hidden">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[50vh] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-600 dark:text-slate-500">No pages found</div>
          ) : (
            filtered.map((item, idx) => (
              <button
                key={item.path}
                onClick={() => handleSelect(item.path)}
                onMouseEnter={() => setSelectedIdx(idx)}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-start transition-colors ${
                  idx === selectedIdx ? 'bg-blue-600/20 text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100/50 dark:hover:bg-slate-800/50'
                }`}
              >
                <span className="text-sm">{item.label}</span>
                <span className="text-xs text-slate-700 dark:text-slate-600">{item.section}</span>
              </button>
            ))
          )}
        </div>

        <div className="flex items-center gap-4 px-4 py-2 border-t border-slate-200 dark:border-slate-800 text-[10px] text-slate-700 dark:text-slate-600">
          <span><kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">↑↓</kbd> navigate</span>
          <span><kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">↵</kbd> open</span>
          <span><kbd className="px-1 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
